/**
 * Created by Simon Zhao on 7/21/21.
 * Description:
 * The configuration section component for a template field.
 * ------ maintenance history ------
 */
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { businessConstants } from '../../../constants/business.constants';
import { dataproviderSelector } from '../../../redux/reducers/dataprovider.reducer';
import { singleTemplateEntityFieldsSelector } from '../../../redux/reducers/template-fields.reducer';
import { Dataprovider, ProviderColumn } from '../../../tamalelibs/models/dataprovider.model';
import { TemplateField } from '../../../tamalelibs/models/template-field.model';
import { MDLMetaEnum, MDLSourceEnum } from '../../../tamalelibs/models/template-mdl.model';

@Component({
    selector: 'mdl-dialog-field-section',
    templateUrl: './mdl-dialog-field-section.component.html',
    styleUrls: ['./mdl-dialog-field-section.component.scss']
})
export class MdlDialogFieldSectionComponent implements OnInit, OnDestroy {

    filteredProviders: Array<Dataprovider> = [];

    filteredProviderColumns: Array<ProviderColumn> = [];

    filteredEntityColumns: Array<TemplateField> = [];

    fieldName: string;

    @Output() sectionDeleting = new EventEmitter<UntypedFormGroup>();

    @Input() typeId = '';

    @Input()
    set sectionFrmGroup(v: UntypedFormGroup) {
        if (v && v !== this._sectionFrmGroup) {
            this._sectionFrmGroup = v;
            this._onSectionFrmGroupChanged(v);
        }
    }

    get sectionFrmGroup(): UntypedFormGroup {
        return this._sectionFrmGroup;
    }

    private _availableProviders: Array<Dataprovider> = [];

    private _availableProviderColumns: Array<ProviderColumn> = [];

    private _availableEntityColumns: Array<TemplateField> = [];

    private _sectionFrmGroup = new UntypedFormGroup({});

    private _suscriptions: Subscription[] = [];

    constructor(private _store: Store<any>) {
    }

    static getCtrlLabel(fg: UntypedFormGroup) {
        if (fg.contains(MDLMetaEnum.controlLabel)) {
            return fg.controls[MDLMetaEnum.controlLabel].value;
        } else {
            return '';
        }
    }

    ngOnInit() {
        this._suscriptions.push(
            this._store.pipe(select(dataproviderSelector)
            ).subscribe(providerList => {
                if (providerList && providerList.length > 0) {
                    const pList = [...providerList];
                    pList.sort(
                        (a: Dataprovider, b: Dataprovider) => a.name.localeCompare(b.name)
                    );
                    this._availableProviders = this.filteredProviders = [...pList];
                    if (this._availableProviders.length > 0 && this.sectionFrmGroup.contains(MDLMetaEnum.providername)) {
                        this._updateProviderColumns(this.sectionFrmGroup.controls[MDLMetaEnum.providername].value);
                    }
                }
            }));

        this._suscriptions.push(
            this._store.pipe(select(singleTemplateEntityFieldsSelector, { id: this.typeId })).subscribe(
                entityFields => {
                    if (entityFields && entityFields.length > 0) {
                        // add current user as a fixed candidate
                        const entityFieldsCopy = [...entityFields];
                        entityFieldsCopy.push(...[businessConstants.currentUserField]);
                        entityFieldsCopy.sort(
                            (a: TemplateField, b: TemplateField) => a.label.localeCompare(b.label)
                        );
                        this._availableEntityColumns = this.filteredEntityColumns = entityFieldsCopy;
                    }
                }
            ));
    }

    ngOnDestroy() {
        this._suscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onProviderFilterChange(keyword: string) {
        this._filterData(keyword, 'filteredProviders', '_availableProviders', 'name');
    }

    onProviderColumnFilterChange(keyword: string) {
        this._filterData(keyword, 'filteredProviderColumns', '_availableProviderColumns', 'name');
    }

    onEntityFilterChange(keyword: string) {
        this._filterData(keyword, 'filteredEntityColumns', '_availableEntityColumns', 'label');
    }

    onSectionDeleting(e: any) {
        this.sectionDeleting.emit(this.sectionFrmGroup);
    }

    private _filterData(keyword: string, filteredArrayVarName: string, candiateArrayVarName: string, colName: string) {
        if (keyword && keyword.trim().length > 0) {
            this[filteredArrayVarName] = this[candiateArrayVarName].filter(
                p => p[colName].toLowerCase().indexOf(keyword.toLowerCase()) !== -1
            );
        } else {
            this[filteredArrayVarName] = [...this[candiateArrayVarName]];
        }
    }

    private _onSectionFrmGroupChanged(fg: UntypedFormGroup) {
        this.fieldName = MdlDialogFieldSectionComponent.getCtrlLabel(fg);

        if (fg.contains(MDLMetaEnum.providername)) {
            fg.controls[MDLMetaEnum.providername].valueChanges.subscribe(name => this._updateProviderColumns(name));
        }

        const fGroup = fg;
        this._suscriptions.push(
            fg.valueChanges.subscribe(fr => {
                if (fg.controls[MDLMetaEnum.mdlsource]) {// disable emitting valueChange event for this setting as it will cause circular calls.
                    fg.get(MDLMetaEnum.mdlsource).setValue(MDLSourceEnum.web, { emitEvent: false });
                }
            }));
    }

    private _updateProviderColumns(providerName: string) {
        const selectedProvider = this._availableProviders.find(p => p.id === providerName);
        if (selectedProvider) {
            this._availableProviderColumns = this.filteredProviderColumns = selectedProvider.columns.sort(
                (a: ProviderColumn, b: ProviderColumn) => a.name.localeCompare(b.name)
            );
        }
        // empty the mdl field selection as the provider has been changed.
        if (!this.sectionFrmGroup.controls[MDLMetaEnum.providername].pristine) {
            this.sectionFrmGroup.controls[MDLMetaEnum.mdlfieldname].reset();
        }
    }

}
